import React, { useEffect, useRef, useState } from 'react';

import { IntlShape, useIntl } from 'react-intl';
import { Alert, Spinner } from 'react-bootstrap';

import instance from 'configs/requests';
import { getApiErrorMessage } from 'utils/api-error';
import { formatMinor } from 'utils/Currency';

interface IOrderCostSummaryProps {
  /** The exact body the order would be placed with, or null when nothing is selected yet. */
  payload: unknown | null;
  locale?: string;
  /** The server's sentence when the quote is refused, null when it is not. */
  onRefused?: (message: string | null) => void;
  /** The catalogue's name for a SKU; without it a refusal names raw SKUs. */
  deviceName?: (sku: string) => string;
}

/** A refusal the portal has its own words for, so the server's English is only a fallback. */
interface IDefaultAccessoryRefusal {
  code?: string;
  items?: { sku: string; parentSku: string | null }[];
}

interface IQuoteLine {
  sku: string;
  name: string;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
}

interface IQuote {
  currencyCode: string;
  lines: IQuoteLine[];
  subtotalMinor: number;
  estimatedTaxMinor: number;
  totalMinor: number;
  availableMinor: number;
  remainingMinor: number;
}

/**
 * Built from the server's SKUs, not its sentence: that sentence is English only and names the
 * first item alone. Null for a refusal we do not recognise, which falls back to what it said.
 */
const refusalCopy = (
  intl: IntlShape,
  data: unknown,
  deviceName: (sku: string) => string
): string | null => {
  const body = data as IDefaultAccessoryRefusal | undefined;
  if (body?.code !== 'DEFAULT_ACCESSORY_WITHOUT_PARENT') return null;
  const items = body.items ?? [];
  if (items.length === 0) return null;
  if (items.length > 1) {
    return intl.formatMessage(
      { id: 'orderCostDefaultAccessoryManyKey' },
      { names: items.map((item) => deviceName(item.sku)).join(', ') }
    );
  }
  const { sku, parentSku } = items[0];
  return parentSku
    ? intl.formatMessage(
        { id: 'orderCostDefaultAccessoryKey' },
        { name: deviceName(sku), parentName: deviceName(parentSku) }
      )
    : intl.formatMessage({ id: 'orderCostDefaultAccessoryNoParentKey' }, { name: deviceName(sku) });
};

/**
 * What this order costs, before it is placed.
 *
 * On a prepaid model the provider is spending a balance they can see, and until this existed the
 * first number anywhere in the flow appeared *after* the order went through (Charles, 2026-09-14).
 *
 * Every figure here comes from the server, from the same `quote()` the order gate prices the real
 * order with. Nothing is computed in the browser on purpose: a client doing its own arithmetic
 * drifts from the gate, and a provider shown one number and charged another has been misled rather
 * than informed.
 *
 * A refused quote, a 422, is shown and blocks the order: the same gate prices the real one, so
 * placing it could only fail the same way. Any other failure means the estimate is unknown rather
 * than the order wrong, so it is a note and blocks nothing.
 */
const OrderCostSummary = ({ payload, locale, onRefused, deviceName }: IOrderCostSummaryProps) => {
  const intl = useIntl();
  const [quote, setQuote] = useState<IQuote | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const onRefusedRef = useRef(onRefused);
  onRefusedRef.current = onRefused;
  const deviceNameRef = useRef(deviceName);
  deviceNameRef.current = deviceName;

  // Leaving the step must not leave the submit button disabled by a verdict no longer on screen.
  useEffect(
    () => () => {
      onRefusedRef.current?.(null);
    },
    []
  );

  const body = payload ? JSON.stringify(payload) : '';

  useEffect(() => {
    const clearRefusal = () => {
      setRefused(null);
      onRefusedRef.current?.(null);
    };

    if (!body) {
      setQuote(null);
      setUnavailable(false);
      clearRefusal();
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    instance
      .post<IQuote>('/clinician-gateway/device/order/quote', JSON.parse(body))
      .then(({ data }) => {
        if (cancelled) return;
        setQuote(data);
        setUnavailable(false);
        clearRefusal();
      })
      .catch((error) => {
        if (cancelled) return;
        setQuote(null);
        const status = (error as { response?: { status?: number } })?.response?.status;
        // 422 is the only verdict the gate gives; anything else may still place.
        if (status !== 422) {
          setUnavailable(true);
          clearRefusal();
          return;
        }
        const message =
          refusalCopy(
            intl,
            (error as { response?: { data?: unknown } })?.response?.data,
            (sku) => deviceNameRef.current?.(sku) ?? sku
          ) ?? getApiErrorMessage(error, intl.formatMessage({ id: 'orderCostRefusedFallbackKey' }));
        setUnavailable(false);
        setRefused(message);
        onRefusedRef.current?.(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Re-running on the callback or on `intl` would re-quote on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body]);

  if (loading && !quote) {
    return (
      <div className="my-3 text-secondary small" data-test="order-cost-loading">
        <Spinner animation="border" size="sm" className="me-2" />
        {intl.formatMessage({ id: 'orderCostLoadingKey' })}
      </div>
    );
  }

  if (refused) {
    return (
      <Alert variant="danger" className="my-3" data-test="order-cost-refused">
        {refused}
      </Alert>
    );
  }

  if (unavailable) {
    return (
      <div className="my-3 text-secondary small" data-test="order-cost-unavailable">
        {intl.formatMessage({ id: 'orderCostUnavailableKey' })}
      </div>
    );
  }

  if (!quote) return null;

  const money = (minor: number) => formatMinor(minor, quote.currencyCode, locale);
  const short = quote.remainingMinor < 0;

  return (
    <div className="my-3" data-test="order-cost-summary">
      <h6>{intl.formatMessage({ id: 'orderCostTitleKey' })}</h6>
      {quote.lines.map((line) => (
        <div className="d-flex justify-content-between small" key={line.sku}>
          <span>
            {line.name}
            {line.quantity > 1 ? ` × ${line.quantity}` : ''}
          </span>
          <span>{money(line.lineTotalMinor)}</span>
        </div>
      ))}
      <div className="d-flex justify-content-between small border-top mt-2 pt-2">
        <span>{intl.formatMessage({ id: 'orderCostSubtotalKey' })}</span>
        <span>{money(quote.subtotalMinor)}</span>
      </div>
      <div className="d-flex justify-content-between small">
        <span>{intl.formatMessage({ id: 'orderCostEstimatedTaxKey' })}</span>
        <span>{money(quote.estimatedTaxMinor)}</span>
      </div>
      <div className="d-flex justify-content-between fw-bold border-top mt-2 pt-2">
        <span>{intl.formatMessage({ id: 'orderCostTotalKey' })}</span>
        <span>{money(quote.totalMinor)}</span>
      </div>
      <div className="d-flex justify-content-between small text-secondary mt-2">
        <span>{intl.formatMessage({ id: 'orderCostAvailableKey' })}</span>
        <span>{money(quote.availableMinor)}</span>
      </div>
      {!short && (
        <div className="d-flex justify-content-between small text-secondary">
          <span>{intl.formatMessage({ id: 'orderCostRemainingKey' })}</span>
          <span>{money(quote.remainingMinor)}</span>
        </div>
      )}
      {short && (
        <Alert variant="warning" className="mt-2 mb-0 py-2 small" data-test="order-cost-short">
          {intl.formatMessage(
            { id: 'orderCostShortfallKey' },
            { amount: money(Math.abs(quote.remainingMinor)) }
          )}
        </Alert>
      )}
      <div className="text-secondary mt-2" style={{ fontSize: '0.75rem' }}>
        {intl.formatMessage({ id: 'orderCostTaxNoteKey' })}
      </div>
    </div>
  );
};

export default OrderCostSummary;
